import React from 'react';

export interface StockBadgeProps {
  quantity: number | null | undefined;
  outOfStockText?: string;
  className?: string;
}

/**
 * StockBadge Component
 * Displays inventory availability in a modern pill badge with dot indicator.
 * Logic:
 * - 0 or negative: Red "Out of stock"
 * - < 5: Amber "Low: N left"
 * - >= 5: Green "N in stock"
 */
export const StockBadge: React.FC<StockBadgeProps> = ({
  quantity,
  outOfStockText = 'Out of stock',
  className = '',
}) => {
  const qty = Number(quantity) || 0;
  const isOutOfStock = qty <= 0;
  const isLowStock = qty > 0 && qty < 5;

  let pillClasses = 'bg-emerald-50 text-emerald-700 border-emerald-200/80';
  let dotColor = 'bg-emerald-500';
  let labelText = `${qty} in stock`;

  if (isOutOfStock) {
    pillClasses = 'bg-rose-50 text-rose-700 border-rose-200/80';
    dotColor = 'bg-rose-500';
    labelText = outOfStockText;
  } else if (isLowStock) {
    pillClasses = 'bg-amber-50 text-amber-700 border-amber-200/80';
    dotColor = 'bg-amber-500';
    labelText = `Low: ${qty} left`;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border whitespace-nowrap shadow-2xs transition-colors ${pillClasses} ${className}`}
      title={labelText}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`} />
      <span>{labelText}</span>
    </span>
  );
};

export default StockBadge;
